import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { Card } from '../../components/common/Card';
import { Heading, Text } from '../../components/common/Typography';
import { Button, BackButton } from '../../components/common';
import ProfileSectionCard from '../../components/profile/ProfileSectionCard';

/**
 * ProfileMyClubs - Club detail page showing performance, H2H, and partnerships
 * Route: /profile/clubs/:clubId
 * Club is the primary context - all data is scoped to this club
 */
const ProfileMyClubs = () => {
  const { currentUser } = useOutletContext();
  const navigate = useNavigate();
  const { clubId } = useParams();
  
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect to club list if no clubId
  useEffect(() => {
    if (!clubId) {
      navigate('/profile/clubs', { replace: true });
      return;
    }
  }, [clubId, navigate]);

  // Fetch selected club details
  useEffect(() => {
    const fetchClub = async () => {
      if (!currentUser?.id || !clubId) return;

      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('players_club')
          .select(`
            club_id,
            club_role,
            clubs:club_id(
              id,
              name,
              description,
              location
            )
          `)
          .eq('player_id', currentUser.id)
          .eq('club_id', clubId)
          .eq('is_active', true)
          .single();

        if (error) throw error;

        if (data && data.clubs) {
          setClub({
            id: data.clubs.id,
            name: data.clubs.name,
            description: data.clubs.description,
            location: data.clubs.location,
            role: data.club_role,
          });
        } else {
          // Club not found or user is not a member - redirect to club list
          navigate('/profile/clubs', { replace: true });
        }
      } catch (err) {
        console.error('Error fetching club:', err);
        // If club not found, redirect to club list
        if (err.code === 'PGRST116') {
          navigate('/profile/clubs', { replace: true });
        } else {
          setError('Failed to load club. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClub();
  }, [currentUser?.id, clubId, navigate]);



  if (!clubId) {
    return null; // Will redirect to club list
  }

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-600">
        Loading club details...
      </div>
    );
  }

  if (error || !club) {
    return (
      <div className="text-center py-12 text-red-600">
        {error || 'Club not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <BackButton onClick={() => navigate('/profile/clubs')} className="mb-4">
        Back to My Clubs
      </BackButton>

      {/* Club Header */}
      <Card variant="default" size="large">
        <div>
          <Heading level={2} className="mb-2">
            {club.name}
          </Heading>
          {club.location && (
            <Text className="text-slate-600 mb-3">
              {club.location}
            </Text>
          )}
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium ${
              ['host', 'admin', 'owner'].includes(club.role)
                ? 'bg-blue-100 text-blue-700'
                : 'bg-slate-100 text-slate-700'
            }`}>
              {['host', 'admin', 'owner'].includes(club.role) ? 'Admin' : club.role}
            </span>
          </div>
        </div>
      </Card>

      {/* Club Sections */}
      {clubId && (
        <div className="space-y-4">
          <Heading level={3} className="mb-4">
            Club Sections
          </Heading>

          {/* Manage Club Section - Only for admins/hosts/owners */}
          {['host', 'admin', 'owner'].includes(club.role) && (
            <ProfileSectionCard
              path={`/profile/clubs/${clubId}/manage`}
              icon="⚙️"
              title="Club Management"
              description="Manage club settings, members, and sessions"
              summary="Administrative controls"
            />
          )}

          {/* Navigate to Club Dashboard - All club features are now there */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/clubs/${clubId}`, { state: { fromMyClubs: true } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/clubs/${clubId}`, { state: { fromMyClubs: true } });
              }
            }}
            className="cursor-pointer transition-all active:scale-[0.98]"
          >
            <Card variant="default" size="medium" className="h-full hover:shadow-md border-slate-200 hover:border-blue-300">
              <div className="flex items-start gap-4">
                <div className="text-4xl flex-shrink-0">🏢</div>
                <div className="flex-1 min-w-0">
                  <Heading level={4} className="mb-1">Club Dashboard</Heading>
                  <Text className="text-sm text-slate-600 mb-2">
                    View performance, matchup records, partnerships, and more
                  </Text>
                  <Text className="text-xs text-slate-500 font-medium">
                    Access all club features in one place
                  </Text>
                </div>
                <div className="flex-shrink-0 text-slate-400">→</div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMyClubs;

